import {Alert, Button, Divider, PasswordInput, Stack, TextInput} from "@mantine/core";
import {useForm} from "@mantine/form";
import {IconAlertCircle, IconLock, IconUser} from "@tabler/icons-react";
import {ReactNode, useCallback, useState} from "react";
import {
  Button as CatalystButton,
  Description as CatalystDescription,
  Field as CatalystField,
  Input as CatalystInput,
  InputGroup as CatalystInputGroup,
  Label as CatalystLabel
} from "catalyst";

export type AppLoginFeature = {
  title: ReactNode;
  description: ReactNode;
};

export type AppLoginPageProps = {
  brandLogo: ReactNode;
  signInTitle?: ReactNode;
  poweredBy?: {
    logo?: ReactNode;
    name: ReactNode;
    url?: string;
  };
  usernameLabel?: string;
  usernameDescription?: string;
  usernamePlaceholder?: string;
  signInButtonLabel?: string;
  invalidCredentialsMessage?: string;
  externalError?: unknown;
  isLegacyLoading?: boolean;
  isSsoAuthenticated?: boolean;
  onLegacySubmit: (values: { username: string | undefined; password: string | undefined }) => Promise<void> | void;
  onSsoLogin: () => void;
  ssoButtonLabel?: string;
  authenticatedSsoButtonLabel?: string;
  formVariant?: "mantine" | "catalyst";
  marketingEyebrow: ReactNode;
  marketingHeadline: ReactNode;
  marketingDescription: ReactNode;
  marketingFeatures: AppLoginFeature[];
  marketingFooter?: ReactNode;
};

export const AppLoginPage = (props: AppLoginPageProps) => {
  const {
    brandLogo,
    signInTitle = "Sign in to your account",
    poweredBy,
    usernameLabel = "Username",
    usernameDescription = "Use the username associated with your account.",
    usernamePlaceholder = "username",
    signInButtonLabel = "Sign in",
    invalidCredentialsMessage = "Login failed. Please verify your username and password.",
    externalError,
    isLegacyLoading = false,
    isSsoAuthenticated = false,
    onLegacySubmit,
    onSsoLogin,
    ssoButtonLabel = "Continue with Single Sign-On",
    authenticatedSsoButtonLabel = "Already signed in with SSO",
    formVariant = "mantine",
    marketingEyebrow,
    marketingHeadline,
    marketingDescription,
    marketingFeatures,
    marketingFooter,
  } = props;

  const form = useForm({
    initialValues: {
      username: undefined,
      password: undefined,
    }
  });

  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    try {
      await onLegacySubmit(values);
      setSubmitError(undefined);
    } catch (err) {
      setSubmitError(invalidCredentialsMessage);
    }
  }, [form, invalidCredentialsMessage, onLegacySubmit]);

  const showLegacyError = submitError || externalError;

  return (
    <div className="min-h-screen bg-white sm:bg-slate-50 flex items-stretch justify-center dark:bg-[#081222] sm:items-center sm:px-4 sm:py-10 dark:sm:bg-[#081222]">
      <div
        className="mx-auto grid min-h-screen w-full grid-cols-1 overflow-hidden bg-white dark:bg-[#0B182E] sm:min-h-0 sm:max-w-6xl sm:rounded-3xl sm:border sm:border-slate-200 sm:shadow-[0_22px_50px_rgba(15,23,42,0.12)] dark:sm:border-white/10 dark:sm:shadow-black/30 lg:grid-cols-2">
        <div className="relative flex min-h-screen items-center px-5 py-8 sm:min-h-0 sm:px-10 sm:py-12 lg:px-12 lg:py-14">
          <div className="w-full max-w-md mx-auto">
            <div className="mb-8">
              <div className="mb-3">
                {brandLogo}
              </div>
              <div>
                <div className="text-base font-semibold text-slate-900 dark:text-white">
                  {signInTitle}
                </div>
                {poweredBy && (
                  <a
                    className="mt-1 inline-flex items-center gap-1.5 text-[0.7rem] font-medium text-slate-500 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                    href={poweredBy.url}
                    target={poweredBy.url ? "_blank" : undefined}
                    rel={poweredBy.url ? "noreferrer" : undefined}
                  >
                    <span>Powered by</span>
                    {poweredBy.logo}
                    <span>{poweredBy.name}</span>
                  </a>
                )}
              </div>
            </div>

            {formVariant === "catalyst" ? (
              <>
                <form onSubmit={form.onSubmit(onFormSubmit)}>
                  <div className="space-y-4">
                    {showLegacyError && (
                      <div
                        role="alert"
                        className="flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                        <IconAlertCircle size={18} className="mt-0.5 shrink-0"/>
                        <span>{submitError || invalidCredentialsMessage}</span>
                      </div>
                    )}

                    <CatalystField>
                      <CatalystLabel>{usernameLabel}</CatalystLabel>
                      <CatalystDescription>{usernameDescription}</CatalystDescription>
                      <CatalystInputGroup>
                        <IconUser data-slot="icon" aria-hidden="true"/>
                        <CatalystInput
                          className="[&_input]:min-h-11 [&_input]:border-slate-200 [&_input]:bg-slate-50 [&_input]:shadow-sm dark:[&_input]:border-white/10 dark:[&_input]:bg-white/5 dark:[&_input]:shadow-none"
                          type="text"
                          placeholder={usernamePlaceholder}
                          required
                          disabled={isLegacyLoading}
                          autoComplete="username"
                          {...form.getInputProps("username")} />
                      </CatalystInputGroup>
                    </CatalystField>

                    <CatalystField>
                      <CatalystLabel>Password</CatalystLabel>
                      <CatalystInputGroup>
                        <IconLock data-slot="icon" aria-hidden="true"/>
                        <CatalystInput
                          className="[&_input]:min-h-11 [&_input]:border-slate-200 [&_input]:bg-slate-50 [&_input]:shadow-sm dark:[&_input]:border-white/10 dark:[&_input]:bg-white/5 dark:[&_input]:shadow-none"
                          type="password"
                          placeholder="••••••••••"
                          required
                          disabled={isLegacyLoading}
                          autoComplete="current-password"
                          {...form.getInputProps("password")} />
                      </CatalystInputGroup>
                    </CatalystField>

                    <CatalystButton
                      type="submit"
                      color="ots"
                      size="lg"
                      disabled={isLegacyLoading}
                      className="w-full">
                      {isLegacyLoading ? "Signing in..." : signInButtonLabel}
                    </CatalystButton>
                  </div>
                </form>

                <div className="my-8 flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-200 dark:bg-white/10"/>
                  <span className="text-xs font-medium text-slate-400 dark:text-zinc-500">or</span>
                  <div className="h-px flex-1 bg-slate-200 dark:bg-white/10"/>
                </div>

                <CatalystButton
                  outline
                  size="lg"
                  onClick={onSsoLogin}
                  disabled={isSsoAuthenticated}
                  className="w-full border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-white dark:shadow-none">
                  {isSsoAuthenticated ? authenticatedSsoButtonLabel : ssoButtonLabel}
                </CatalystButton>
              </>
            ) : (
              <>
                <form onSubmit={form.onSubmit(onFormSubmit)}>
                  <Stack spacing="md">
                    {showLegacyError && (
                      <Alert
                        variant="light"
                        color="red"
                        radius="md"
                        icon={<IconAlertCircle size={18}/>}>
                        {submitError || invalidCredentialsMessage}
                      </Alert>
                    )}

                    <TextInput
                      label={usernameLabel}
                      description={usernameDescription}
                      placeholder={usernamePlaceholder}
                      icon={<IconUser size={18}/>}
                      required
                      disabled={isLegacyLoading}
                      autoComplete="username"
                      size="md"
                      radius="md"
                      classNames={{
                        input: "!bg-slate-50 !border-slate-200 !text-slate-900 dark:!bg-zinc-900 dark:!border-zinc-700 dark:!text-white",
                        label: "!text-slate-600 dark:!text-zinc-300",
                        description: "!text-slate-400 dark:!text-zinc-500",
                      }}
                      {...form.getInputProps("username")} />

                    <PasswordInput
                      label="Password"
                      placeholder="••••••••••"
                      icon={<IconLock size={18}/>}
                      required
                      disabled={isLegacyLoading}
                      autoComplete="current-password"
                      size="md"
                      radius="md"
                      classNames={{
                        input: "!bg-slate-50 !border-slate-200 !text-slate-900 dark:!bg-zinc-900 dark:!border-zinc-700 dark:!text-white",
                        innerInput: "!text-slate-900 dark:!text-white",
                        label: "!text-slate-600 dark:!text-zinc-300",
                      }}
                      {...form.getInputProps("password")} />

                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      radius="md"
                      loading={isLegacyLoading}
                      className="!bg-[#12284C] hover:!bg-[#0f2142] !text-white !font-semibold">
                      {signInButtonLabel}
                    </Button>
                  </Stack>
                </form>

                <Divider
                  my="xl"
                  label="or"
                  labelPosition="center"
                  className="border-slate-200"
                  labelProps={{
                    style: {color: "#9CA3AF", fontSize: "0.75rem"},
                  }}
                />

                <Stack spacing="sm">
                  <Button
                    variant="outline"
                    fullWidth
                    size="md"
                    radius="md"
                    onClick={onSsoLogin}
                    disabled={isSsoAuthenticated}
                    className="!border-slate-300 !text-slate-800 hover:!bg-slate-50 dark:!border-zinc-700 dark:!text-zinc-100 dark:hover:!bg-zinc-800">
                    {isSsoAuthenticated ? authenticatedSsoButtonLabel : ssoButtonLabel}
                  </Button>
                </Stack>
              </>
            )}
          </div>
        </div>

        <div className="relative hidden lg:block overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-slate-100 dark:from-[#081222] dark:via-[#0B182E] dark:to-[#12284C]"/>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-[#12284C]/5 blur-3xl dark:bg-white/5"/>
            <div className="absolute -bottom-16 -left-8 h-64 w-64 rounded-full bg-[#E1251B]/10 blur-3xl dark:bg-[#E1251B]/15"/>
          </div>

          <div className="relative h-full flex flex-col justify-between p-10 text-slate-900 dark:text-white">
            <div>
              <p
                className="inline-flex items-center rounded-full bg-[#12284C]/5 px-3 py-1 text-[0.7rem] font-medium uppercase tracking-[0.18em] text-[#12284C] dark:bg-white/10 dark:text-zinc-200">
                {marketingEyebrow}
              </p>

              <h2 className="mt-6 text-3xl font-semibold tracking-tight leading-tight sm:text-4xl text-[#12284C] dark:text-white">
                {marketingHeadline}
              </h2>

              <p className="mt-4 text-sm text-slate-600 max-w-md dark:text-zinc-300">
                {marketingDescription}
              </p>
            </div>

            <div className="mt-10 space-y-4">
              {marketingFeatures.map((feature, index) => (
                <div className="flex items-start gap-3" key={index}>
                  <span
                    className={[
                      "mt-0.5 h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold",
                      index === 0 ? "bg-[#12284C]/10 text-[#12284C] dark:bg-white/10 dark:text-white" : "",
                      index === 1 ? "bg-[#E1251B]/10 text-[#E1251B] dark:bg-[#E1251B]/15 dark:text-red-300" : "",
                      index > 1 ? "bg-slate-900/5 text-slate-700 dark:bg-white/10 dark:text-zinc-200" : "",
                    ].join(" ")}>
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      {feature.title}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-zinc-400">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {marketingFooter}
          </div>
        </div>
      </div>
    </div>
  )
};
